import type { EpisodeSummaryResponse } from '../../services/api/types.gen';

type MovedEpisodeFields = Pick<
  EpisodeSummaryResponse,
  'status' | 'taken_over_at'
> & { id?: string };

/**
 * The open period whose Take Over moved this chart to Roger: the earliest
 * Take Over among active periods. Null when no open period was taken over.
 */
export function chartMovedEpisode<T extends MovedEpisodeFields>(
  episodes: T[]
): (T & { taken_over_at: string }) | null {
  let earliest: (T & { taken_over_at: string }) | null = null;
  for (const episode of episodes) {
    const takenOverAt = episode.taken_over_at;
    if (episode.status !== 'active' || !takenOverAt) continue;
    if (!earliest || takenOverAt < earliest.taken_over_at) {
      earliest = { ...episode, taken_over_at: takenOverAt };
    }
  }
  return earliest;
}

/**
 * The moment Take Over moved this chart to Roger, while the taken-over
 * period is still open. Null when no open period was taken over.
 */
export function chartMovedAt(episodes: MovedEpisodeFields[]): string | null {
  return chartMovedEpisode(episodes)?.taken_over_at ?? null;
}

type ChartsFromFields = Pick<
  EpisodeSummaryResponse,
  'start_date' | 'end_date' | 'roger_charts_from'
>;

/**
 * Where an episode sits against the day Roger charts it from (EMR-2889),
 * read from its summary's `roger_charts_from`: the server's effective day.
 * Null when the summary carries none. The day itself is Roger's.
 */
export function episodeAgainstChartsFrom(
  episode: ChartsFromFields
): 'ended_before' | 'contains' | 'starts_on_or_after' | null {
  const day = episode.roger_charts_from;
  if (!day) return null;
  if (episode.end_date < day) return 'ended_before';
  if (episode.start_date >= day) return 'starts_on_or_after';
  return 'contains';
}
